import {
  Form,
  FormField,
  Input,
  Textarea,
  Select,
  SpaceBetween,
  Button,
  Container,
  Header,
  Box,
  ColumnLayout,
  Multiselect,
  SelectProps,
  MultiselectProps,
} from '@cloudscape-design/components';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { CreateRuleInput, RuleInput, InputSource, Severity, RuleStatus, ExpressionLanguage } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

type WidenedExpressionLanguage = ExpressionLanguage | 'llm' | 'zelkova';

// Constants for form options
const SEVERITY_OPTIONS: SelectProps.Option[] = [
  { label: 'Critical', value: 'CRITICAL' },
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
];

const STATUS_OPTIONS: SelectProps.Option[] = [
  { label: 'Draft', value: 'draft' },
  { label: 'Dry Run', value: 'dry_run' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const LANGUAGE_OPTIONS: SelectProps.Option[] = [
  { label: 'Sangheli 1.0', value: 'sangheli-1.0' },
  { label: 'LLM', value: 'llm' },
  { label: 'Zelkova', value: 'zelkova' },
];

const SOURCE_TYPE_OPTIONS: SelectProps.Option[] = [
  { label: 'Discovery', value: 'discovery' },
  { label: 'Arbiter', value: 'arbiter' },
  { label: 'Call', value: 'call' },
  { label: 'Rule', value: 'rule' },
];

// Validation patterns
const RULE_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]{2,127}$/;

interface InputFormState {
  name: string;
  type: string;
  sourceType: string;
  discoveryType?: string;
  discoveryFilter?: string;
  arbiterKey?: string;
  callKey?: string;
  ruleTarget?: string;
}

export function CreateRulePage() {
  useDocumentTitle('Create Rule');
  const navigate = useNavigate();
  const client = useArbiterClient();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Basic Info
  const [ruleName, setRuleName] = useState('');
  const [description, setDescription] = useState('');
  const [appliesTo, setAppliesTo] = useState<MultiselectProps.Option[]>([]);
  const [severity, setSeverity] = useState<SelectProps.Option | null>(SEVERITY_OPTIONS[2]); // Default: Medium
  const [ruleStatus, setRuleStatus] = useState<SelectProps.Option | null>(STATUS_OPTIONS[0]); // Default: Draft

  // Inputs
  const [inputs, setInputs] = useState<InputFormState[]>([]);

  // Evaluation
  const [language, setLanguage] = useState<SelectProps.Option | null>(LANGUAGE_OPTIONS[0]);
  const [expression, setExpression] = useState('');

  // Validation errors
  const [ruleNameError, setRuleNameError] = useState('');

  const validateRuleName = (name: string): boolean => {
    if (!name) {
      setRuleNameError('Rule name is required');
      return false;
    }
    if (!RULE_NAME_PATTERN.test(name)) {
      setRuleNameError('Rule name must start with a letter and contain only letters, numbers, hyphens, and underscores (3-128 characters)');
      return false;
    }
    setRuleNameError('');
    return true;
  };

  const validateBasicInfo = (): boolean => {
    const isNameValid = validateRuleName(ruleName);
    if (!isNameValid) return false;
    if (appliesTo.length === 0) {
      setError('At least one resource type is required');
      return false;
    }
    return true;
  };

  const validateInputs = (): boolean => {
    for (const input of inputs) {
      if (!input.name || !input.type) {
        setError('All inputs must have a name and type');
        return false;
      }
      if (!input.sourceType) {
        setError('All inputs must have a source type');
        return false;
      }
      // Validate source-specific fields
      if (input.sourceType === 'discovery' && !input.discoveryType) {
        setError('Discovery source requires a discovery type');
        return false;
      }
      if (input.sourceType === 'arbiter' && !input.arbiterKey) {
        setError('Arbiter source requires a key');
        return false;
      }
      if (input.sourceType === 'call' && !input.callKey) {
        setError('Call source requires a key');
        return false;
      }
      if (input.sourceType === 'rule' && !input.ruleTarget) {
        setError('Rule source requires a target');
        return false;
      }
    }
    return true;
  };

  const validateEvaluation = (): boolean => {
    if (!language) {
      setError('Language is required');
      return false;
    }
    if (!expression.trim()) {
      setError('Expression is required');
      return false;
    }
    return true;
  };

  const addInput = () => {
    setInputs([...inputs, {
      name: '',
      type: '',
      sourceType: 'discovery',
    }]);
  };

  const removeInput = (index: number) => {
    setInputs(inputs.filter((_, i) => i !== index));
  };

  const updateInput = (index: number, field: keyof InputFormState, value: string) => {
    const updated = [...inputs];
    updated[index] = { ...updated[index], [field]: value };
    setInputs(updated);
  };

  const convertInputsToRuleInputs = (): RuleInput[] => {
    return inputs.map(input => {
      let source: InputSource;

      if (input.sourceType === 'discovery') {
        source = { discovery: { discoveryType: input.discoveryType || 'DESCRIBE_RESOURCE', filter: input.discoveryFilter ? { language: 'yaql', expression: input.discoveryFilter } : undefined } } as InputSource.DiscoveryMember;
      } else if (input.sourceType === 'arbiter') {
        source = { arbiter: { key: input.arbiterKey || '' } } as InputSource.ArbiterMember;
      } else if (input.sourceType === 'call') {
        source = { call: { key: input.callKey || '' } } as InputSource.CallMember;
      } else {
        source = { rule: { target: input.ruleTarget || '' } } as InputSource.RuleMember;
      }

      return {
        name: input.name,
        source,
        type: input.type,
      };
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);

    try {
      const request: CreateRuleInput = {
        ruleName,
        description: description || undefined,
        appliesTo: appliesTo.map(opt => opt.value || ''),
        inputs: convertInputsToRuleInputs(),
        evaluation: {
          language: (language?.value as WidenedExpressionLanguage as ExpressionLanguage) || 'sangheli-1.0',
          expression,
        },
        severity: severity?.value as Severity | undefined,
        ruleStatus: ruleStatus?.value as RuleStatus | undefined,
      };

      const response = await client.createRule(request);

      // Navigate to the created rule's detail page
      navigate(`/rules/${response.rule?.ruleName ?? ruleName}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create rule';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = async () => {
    setError(null);
    if (!validateBasicInfo()) return;
    if (!validateInputs()) return;
    if (!validateEvaluation()) return;
    await handleSubmit();
  };

  return (
    <Form
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => navigate('/rules')}>Cancel</Button>
          <Button variant="primary" loading={submitting} onClick={handleFormSubmit}>Create rule</Button>
        </SpaceBetween>
      }
      header={<Header variant="h1" description="Define a new rule for evaluation">Create Rule</Header>}
      errorText={error}
    >
      <SpaceBetween size="l">
        {/* Basic Information */}
        <Container header={<Header variant="h2">Basic Information</Header>}>
          <SpaceBetween size="l">
            <FormField
              label="Rule Name"
              description="Must start with a letter and contain only letters, numbers, hyphens, and underscores (3-128 characters)"
              errorText={ruleNameError}
              constraintText="Required"
            >
              <Input
                value={ruleName}
                onChange={({ detail }) => {
                  setRuleName(detail.value);
                  validateRuleName(detail.value);
                }}
                placeholder="my-security-rule"
              />
            </FormField>

            <FormField
              label="Description"
              description="A brief description of what this rule validates"
            >
              <Textarea
                value={description}
                onChange={({ detail }) => setDescription(detail.value)}
                placeholder="Enter a description..."
                rows={3}
              />
            </FormField>

            <FormField
              label="Applies To"
              description="Resource types this rule applies to"
              constraintText="Required - Select at least one"
            >
              <Multiselect
                selectedOptions={appliesTo}
                onChange={({ detail }) => setAppliesTo([...detail.selectedOptions])}
                options={[
                  { label: 'AWS EC2 Instance', value: 'aws::ec2::instance' },
                  { label: 'AWS S3 Bucket', value: 'aws::s3::bucket' },
                  { label: 'AWS Lambda Function', value: 'aws::lambda::function' },
                  { label: 'AWS IAM Role', value: 'aws::iam::role' },
                  { label: 'AWS RDS Instance', value: 'aws::rds::instance' },
                ]}
                placeholder="Select resource types"
                filteringType="auto"
              />
            </FormField>

            <ColumnLayout columns={2}>
              <FormField label="Severity" description="Severity level of rule violations">
                <Select
                  selectedOption={severity}
                  onChange={({ detail }) => setSeverity(detail.selectedOption)}
                  options={SEVERITY_OPTIONS}
                />
              </FormField>

              <FormField label="Status" description="Initial status of the rule">
                <Select
                  selectedOption={ruleStatus}
                  onChange={({ detail }) => setRuleStatus(detail.selectedOption)}
                  options={STATUS_OPTIONS}
                />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        </Container>

        {/* Inputs */}
        <Container
          header={
            <Header
              variant="h2"
              actions={<Button onClick={addInput}>Add Input</Button>}
            >
              Inputs
            </Header>
          }
        >
          <SpaceBetween size="l">
            {inputs.length === 0 ? (
              <Box textAlign="center" color="text-body-secondary">
                No inputs defined. Click "Add Input" to create one.
              </Box>
            ) : (
              inputs.map((input, index) => (
                <Container
                  key={index}
                  header={
                    <Header
                      variant="h3"
                      actions={
                        <Button onClick={() => removeInput(index)} variant="link">
                          Remove
                        </Button>
                      }
                    >
                      Input {index + 1}
                    </Header>
                  }
                >
                  <SpaceBetween size="m">
                    <ColumnLayout columns={2}>
                      <FormField label="Name" constraintText="Required">
                        <Input
                          value={input.name}
                          onChange={({ detail }) => updateInput(index, 'name', detail.value)}
                          placeholder="inputName"
                        />
                      </FormField>

                      <FormField label="Type" constraintText="Required">
                        <Input
                          value={input.type}
                          onChange={({ detail }) => updateInput(index, 'type', detail.value)}
                          placeholder="string"
                        />
                      </FormField>
                    </ColumnLayout>

                    <FormField label="Source Type" constraintText="Required">
                      <Select
                        selectedOption={SOURCE_TYPE_OPTIONS.find(opt => opt.value === input.sourceType) || SOURCE_TYPE_OPTIONS[0]}
                        onChange={({ detail }) => updateInput(index, 'sourceType', detail.selectedOption.value || 'discovery')}
                        options={SOURCE_TYPE_OPTIONS}
                      />
                    </FormField>

                    {input.sourceType === 'discovery' && (
                      <>
                        <FormField label="Discovery Type" constraintText="Required">
                          <Select
                            selectedOption={input.discoveryType ? { label: input.discoveryType, value: input.discoveryType } : { label: 'DESCRIBE_RESOURCE', value: 'DESCRIBE_RESOURCE' }}
                            onChange={({ detail }) => updateInput(index, 'discoveryType', detail.selectedOption.value || 'DESCRIBE_RESOURCE')}
                            options={[
                              { label: 'DESCRIBE_RESOURCE', value: 'DESCRIBE_RESOURCE' },
                            ]}
                          />
                        </FormField>
                        <FormField label="Filter">
                          <Input
                            value={input.discoveryFilter || ''}
                            onChange={({ detail }) => updateInput(index, 'discoveryFilter', detail.value)}
                            placeholder="Optional YAQL filter expression"
                          />
                        </FormField>
                      </>
                    )}

                    {input.sourceType === 'arbiter' && (
                      <FormField label="Key" constraintText="Required">
                        <Input
                          value={input.arbiterKey || ''}
                          onChange={({ detail }) => updateInput(index, 'arbiterKey', detail.value)}
                          placeholder="arbiter.key"
                        />
                      </FormField>
                    )}

                    {input.sourceType === 'call' && (
                      <FormField label="Key" constraintText="Required">
                        <Input
                          value={input.callKey || ''}
                          onChange={({ detail }) => updateInput(index, 'callKey', detail.value)}
                          placeholder="call.key"
                        />
                      </FormField>
                    )}

                    {input.sourceType === 'rule' && (
                      <FormField label="Target Rule" constraintText="Required">
                        <Input
                          value={input.ruleTarget || ''}
                          onChange={({ detail }) => updateInput(index, 'ruleTarget', detail.value)}
                          placeholder="target-rule-name"
                        />
                      </FormField>
                    )}
                  </SpaceBetween>
                </Container>
              ))
            )}
          </SpaceBetween>
        </Container>

        {/* Evaluation */}
        <Container header={<Header variant="h2">Evaluation</Header>}>
          <SpaceBetween size="l">
            <FormField label="Language" constraintText="Required">
              <Select
                selectedOption={language}
                onChange={({ detail }) => setLanguage(detail.selectedOption)}
                options={LANGUAGE_OPTIONS}
              />
            </FormField>

            <FormField
              label="Expression"
              description="The expression that evaluates the rule"
              constraintText="Required"
            >
              <Textarea
                value={expression}
                onChange={({ detail }) => setExpression(detail.value)}
                placeholder="input1.value == 'expected' && input2.count > 0"
                rows={10}
              />
            </FormField>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Form>
  );
}
